/**
 * « Appareils connectes » (« Devices » de Bitwarden, « Sessions » de Proton
 * Pass) : lecture, cote PWA, de `GET /v1/sessions` -- une ligne par connexion
 * encore renouvelable, avec sa date de connexion, sa derniere activite et le
 * navigateur de la connexion d'origine.
 *
 * Le `User-Agent` est une chaine choisie par QUI s'est connecte : comme pour
 * l'activite du compte, on ne l'affiche qu'a travers la liste fermee de
 * `describeUserAgent`, et chaque ligne recue est revalidee -- une ligne
 * illisible est jetee, jamais affichee a moitie. L'identifiant ne sert qu'a
 * designer l'appareil a deconnecter : forme stricte, sans quoi la ligne tombe.
 */

export interface ConnectedDevice {
  readonly id: string;
  /** `null` : session ouverte avant le suivi des appareils (date inconnue). */
  readonly createdAt: number | null;
  readonly lastSeenAt: number | null;
  readonly userAgent?: string;
  readonly current: boolean;
}

/** base64url sans bourrage ou UUID : la meme forme que la route `DELETE /v1/sessions/:id` accepte. */
const DEVICE_ID_SHAPE = /^[A-Za-z0-9_-]{1,64}$/u;
const MAX_USER_AGENT_LENGTH = 128;

function isDate(value: unknown): value is number | null {
  return value === null || (typeof value === 'number' && Number.isFinite(value));
}

/** Cet appareil d'abord, puis le plus recemment actif ; une date inconnue en dernier. */
export function parseConnectedDevices(body: unknown): ConnectedDevice[] {
  const raw = (body as { sessions?: unknown } | null | undefined)?.sessions;
  if (!Array.isArray(raw)) throw new Error('liste des appareils illisible');
  const devices: ConnectedDevice[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    if (typeof item !== 'object' || item === null) continue;
    const { id, createdAt, lastSeenAt, userAgent, current } = item as Record<string, unknown>;
    if (typeof id !== 'string' || !DEVICE_ID_SHAPE.test(id) || seen.has(id)) continue;
    if (!isDate(createdAt) || !isDate(lastSeenAt) || typeof current !== 'boolean') continue;
    seen.add(id);
    const device: { -readonly [K in keyof ConnectedDevice]: ConnectedDevice[K] } = { id, createdAt, lastSeenAt, current };
    if (typeof userAgent === 'string' && userAgent.length > 0) device.userAgent = userAgent.slice(0, MAX_USER_AGENT_LENGTH);
    devices.push(device);
  }
  return devices.sort((a, b) => Number(b.current) - Number(a.current) || (b.lastSeenAt ?? -1) - (a.lastSeenAt ?? -1));
}
